import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, shareReplay, map, retryWhen, tap} from 'rxjs/operators';

import {HttpService} from '../http.service';
import FundOrderBy from 'src/app/shared/enums/FundOrderBy.enum';
import {GpFundListItemReqRes, GpFundReqRes} from 'src/app/dashboard/funds/GpFundReqRes.model';
import {GpFundPollingReqRes} from 'src/app/dashboard/funds/gp-fund/GpFundPollingReqRes.model';
import HTTP_STATUS_CODES from 'src/app/shared/enums/HttpStatusCodesEnum';
import {RoutingService} from '../routing.service';
import {FundAndFundraisingReqRes} from 'src/app/dashboard/funds/components/create-fund/FundAndFundraisingRequest';
import HoldingStatus from 'src/app/shared/enums/HoldingStatus.enum';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';
import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import {SearchOptionsRequest} from 'src/app/shared/models/SearchOptionsRequest.model';
import {IdValue} from '../../shared/models/IdValue.model';
import {GpHoldingOverviewItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingOverviewItemReqRes';

@Injectable({
  providedIn: 'root'
})
export class GpFundDataService {
  public readonly FUND_BASE_ENDPOINT = 'gp/funds';
  private readonly CREATE_FUND_ENDPOINT = this.FUND_BASE_ENDPOINT;
  public readonly discriminator = HoldingDiscriminator.Fund;

  constructor(private http: HttpService, private router: Router, private routingService: RoutingService) {
  }

  getFundsList(orderBy: FundOrderBy = FundOrderBy.CreatedDateNewerFirst): Observable<GpFundListItemReqRes[]> {
    return this.http.getTyped<GpFundListItemReqRes[]>(`${this.FUND_BASE_ENDPOINT}?orderBy=${orderBy}`)
      .pipe(
        map((funds) => funds.map<GpFundListItemReqRes>(fund =>
          ({...fund, discriminator: this.discriminator}))),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  getDealsList(orderBy: FundOrderBy = FundOrderBy.NameAsc): Observable<GpHoldingOverviewItemReqRes[]> {
    return this.http.getTyped<GpHoldingOverviewItemReqRes[]>(`${this.FUND_BASE_ENDPOINT}/overview?orderBy=${orderBy}`).pipe(
      map((assets) => assets.map<GpHoldingOverviewItemReqRes>((asset) =>
        ({...asset, discriminator: this.discriminator}))),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  getDealsBalances() {
    return this.http.getTyped<IdValue<number>[]>(`${this.FUND_BASE_ENDPOINT}/overview/balances`).pipe(
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  detectChanges(fundId: number, fundraisingId: number, distributionId: number, changedSince: Date): Observable<GpFundPollingReqRes> {
    const pollRequest = {
      fundraisingId,
      distributionId,
      changedSince
    };

    return this.http
      .postTyped<GpFundPollingReqRes>(`${this.FUND_BASE_ENDPOINT}/${fundId}/detectChanges`, pollRequest)
      .pipe(
        map((response) => {
          return response ?({...response, fund: {...response.fund, discriminator: this.discriminator}}) : null
        }),
        retryWhen((error) =>
          error.pipe(
            tap((err) => {
              if (err.status !== 0) { // When Azure app service reaches timeout (230 seconds) error received with 0 status
                throw error;
              }
            })
          )),
        catchError((err) => {
          // if (err.status === 0) {
          //   console.log('detect changes request has failed');
          //   return this.detectChanges(fundId, fundraisingId, changedSince);
          // }
          // else
          if (err.status === HTTP_STATUS_CODES.NOT_FOUND) {
            // if fund polling (initial fundraising): return to funds list
            if (fundraisingId === 0) {
              this.router.navigateByUrl(this.routingService.gpFundsPage);
            } else {
              this.router.navigateByUrl(this.routingService.gpFundPage(fundId));
            }
          }
          return throwError(() => err);
        })
      );
  }

  getFundById(fundId: number): Observable<GpFundReqRes> {
    const url = `${this.FUND_BASE_ENDPOINT}/${fundId}`;
    return this.http.getTyped<GpFundReqRes>(url).pipe(
      map((fund) => this.addDiscriminatorToFund(fund)),
      shareReplay());
  }

  /** Get a list of all holdings that the fund is involved in.
   * They can be fund-assets inside the fund, or holding in which the fund invested. */
  getFundHoldings(fundId: number, options: SearchOptionsRequest) {
    options.pageSize = 1000;
    options.pageNumber = 0;
    let queryString = `?sortOrder=${options.sortOrder}&orderBy=${options.orderBy}&pageNumber=${options.pageNumber}&pageSize=${options.pageSize}`;
    const url = `${this.FUND_BASE_ENDPOINT}/${fundId}/holdings` + queryString;
    return this.http.getTyped<GpHoldingListItemReqRes[]>(url).pipe(
      shareReplay());
  }

  createFund(model: FundAndFundraisingReqRes): Observable<FundAndFundraisingReqRes> {
    return this.http.postTyped<FundAndFundraisingReqRes>(this.CREATE_FUND_ENDPOINT, model).pipe(
      map((response) => this.addDiscriminatorToFundAndFundraising(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  createDraftFund(model: GpFundReqRes): Observable<GpFundReqRes> {
    return this.http.postTyped<GpFundReqRes>(`${this.CREATE_FUND_ENDPOINT}/draft`, model).pipe(
      map((asset) => this.addDiscriminatorToFund(asset)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  updateFund(fundId: number, model: FundAndFundraisingReqRes, skipNotification = false): Observable<FundAndFundraisingReqRes> {
    return this.http.putTyped<FundAndFundraisingReqRes>(`${this.FUND_BASE_ENDPOINT}/${fundId}?skipNotification=${skipNotification}`, model).pipe(
      map((response) => this.addDiscriminatorToFundAndFundraising(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  updateDraftFund(fundId: number, model: GpFundReqRes): Observable<void> {
    return this.http.putTyped<void>(`${this.FUND_BASE_ENDPOINT}/draft/${fundId}`, model).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  updateFundVisibility(model: GpHoldingOverviewItemReqRes): Observable<boolean> {
    return this.http.putTyped<boolean>(`${this.FUND_BASE_ENDPOINT}/visibility`, model).pipe(
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  moveFundToUnderManagement(fundId: number, model: FundAndFundraisingReqRes): Observable<FundAndFundraisingReqRes> {
    return this.http.patchTyped<FundAndFundraisingReqRes>(`${this.FUND_BASE_ENDPOINT}/${fundId}/move-to-under-management`, model).pipe(
      map((response) => this.addDiscriminatorToFundAndFundraising(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  moveFundraisingToCompleted(fundId: number, model: FundAndFundraisingReqRes): Observable<FundAndFundraisingReqRes> {
    return this.http.patchTyped<FundAndFundraisingReqRes>(`${this.FUND_BASE_ENDPOINT}/${fundId}/move-fundraising-to-completed`, model).pipe(
      map((response) => this.addDiscriminatorToFundAndFundraising(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  updateStatus(fundId: number, holdingStatus: HoldingStatus, draftSendEmailsWhenMovingToUnderManagement = false) {
    const model = new GpFundReqRes();
    model.id = fundId;
    model.status = holdingStatus;
    return this.http.putTyped<GpFundReqRes>(
      `${this.FUND_BASE_ENDPOINT}/${fundId}/status?sendEmails=${draftSendEmailsWhenMovingToUnderManagement}`, model)
      .pipe(
        map((fund) => this.addDiscriminatorToFund(fund)),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  deleteFund(fundId: number) {
    return this.http.deleteTyped<void>(`${this.FUND_BASE_ENDPOINT}/${fundId}`).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  isFundNameExists(fundName: string, fundIdToExclude: number = 0) {
    fundName = fundName.trim();
    const url = `${this.FUND_BASE_ENDPOINT}/fund-name-exists-check`;
    return this.http.postTyped<boolean>(url, {fundName, fundIdToExclude}).pipe(shareReplay(1));
  }

  /** Get a list of all the funds that can invest in other holdings */
  getFundsForInvestment(): Observable<GpFundListItemReqRes[]> {
    return this.http.getTyped<GpFundListItemReqRes[]>(`${this.FUND_BASE_ENDPOINT}/as-investors`).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  private addDiscriminatorToFund(fund: GpHoldingListItemReqRes) {
    return {...fund, discriminator: this.discriminator} as GpFundReqRes;
  }

  private addDiscriminatorToFundAndFundraising(fundAndFundraising: FundAndFundraisingReqRes) {
    return {
      ...fundAndFundraising,
      fund: this.addDiscriminatorToFund(fundAndFundraising.fund)
    } as FundAndFundraisingReqRes;
  }
}
